import { Alert, Box, Typography } from '@mui/material'
import { EntityFieldDataChange, OrderStatus, OrderUpdateResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { InitializationWrapper } from '@planning/components'
import { useOrderBreadcrumbsV2 } from '@planning/hooks/useOrderBreadcrumbsV2'
import containerJourneysService from '@planning/services/containerJourneys'
import { formatDateTime } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, useMinimalsTheme } from '@tom-ui/ui'
import { authStore, useAsyncFetch } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router'
import { ContainerYardOperationViewStore } from '../VesselVisit/Components'
import { OrderUpdateNotification } from './components/Organisms/OrderUpdateNotification'
import { ReviewForm } from './components/ReviewForm/ReviewForm'
import { EditOrderPageViewStore } from './stores/EditOrderPageViewStore'
import { OrderUpdatesViewStore } from './stores/OrderUpdatesViewStore'
import { OrderFormStore } from './V2/stores/OrderFormStore'
import { EditOrderPageHeader } from './V2/templates/EditOrderPageHeader'

export type HandleOrderPageLocationState = {
  returnPath: string
  returnPathAdditionalParams?: ReturnPathAdditionalParams
}

export type ReturnPathAdditionalParams = {
  containerNumber?: boolean
}

const EntityFieldDataChangeList: FC<{ update: OrderUpdateResponseDto }> = ({ update }) =>
  update.entityFieldDataChanges.map(change => (
    <EntityFieldDataChangeText key={change.name} change={change} />
  ))

const EntityFieldDataChangeText: FC<{ change: EntityFieldDataChange }> = ({ change }) => (
  <Box>
    {change.name} has changed from <strong>{change.prevValue}</strong> to{' '}
    <strong>{change.newValue}</strong>
  </Box>
)

export const HandleOrderPage: FC<{
  containerYardOperationViewStore: ContainerYardOperationViewStore
}> = observer(props => {
  const { containerYardOperationViewStore } = props

  const {
    appViewStore,
    orderItemStore,
    orderUpdateItemStore,
    containerItemStore,
    containerJourneyItemStore,
    tenantStore,
    weightClassItemStore,
  } = usePlanningStore()
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const isAdmin = authStore.isAdmin()

  const { getRouteBreadcrumbs } = useOrderBreadcrumbsV2()

  const { orderId: orderIdParam } = useParams()
  const orderId = Number(orderIdParam)

  const orderUpdatesViewStore = useMemo(
    () => new OrderUpdatesViewStore(orderUpdateItemStore),
    [orderUpdateItemStore],
  )

  const formStore = useMemo(() => new OrderFormStore(), [])

  const editOrderPageViewStore = useMemo(
    () =>
      new EditOrderPageViewStore(
        orderId,
        formStore,
        tenantStore,
        containerItemStore,
        containerJourneyItemStore,
        orderItemStore,
        containerYardOperationViewStore,
      ),
    [
      orderId,
      formStore,
      tenantStore,
      containerItemStore,
      containerJourneyItemStore,
      orderItemStore,
      containerYardOperationViewStore,
    ],
  )

  useEffect(() => {
    formStore.setData(editOrderPageViewStore.initialValues)
  }, [editOrderPageViewStore.initialValues, formStore])

  const updates = computed(() =>
    orderUpdatesViewStore.entityFieldUpdates.map(update => ({
      title: <EntityFieldDataChangeList key={update.id} update={update} />,
      subtitle: `${formatDateTime(update.received)} - ${update.source}`,
    })),
  ).get()

  // TODO [Order Rework] Check if there's a better way to fetch all the data needed for the page
  const { loading: orderLoading, error: orderError } = useAsyncFetch(async () => {
    orderUpdatesViewStore.setOrderId(orderId)
    await orderItemStore.fetchByOrderId(orderId)
  }, [orderId, orderItemStore, orderUpdatesViewStore])

  const { loading: visitLoading, error: visitError } = useAsyncFetch(async () => {
    const journey = await containerJourneysService.getByOrderIds([orderId])
    containerJourneyItemStore.upsertBulk(journey)
  }, [containerJourneyItemStore, orderId])

  useAsyncFetch(async () => {
    if (editOrderPageViewStore.order?.containerId) {
      await containerItemStore.fetchById(editOrderPageViewStore.order.containerId)
      await weightClassItemStore.fetch()
    }
  }, [containerItemStore, editOrderPageViewStore.order?.containerId, weightClassItemStore])

  const onSubmit = async () => {
    try {
      editOrderPageViewStore.setIsLoading(true)

      await editOrderPageViewStore.submit()
      appViewStore.setShowAlert('success', t('savedSuccessfully', 'Saved Successfully'))
    } catch (error) {
      appViewStore.setShowAlert('error', t('failedToSave', 'Failed to save'))
    } finally {
      editOrderPageViewStore.setIsLoading(false)
    }
  }

  const isOrderFulfilledAndUserNonAdmin = useMemo(
    () =>
      !isAdmin &&
      // TODO [Order Rework] Refactor this once the new orders page is fully implemented
      (editOrderPageViewStore.journey?.status || editOrderPageViewStore.order?.status) ===
        OrderStatus.Fulfilled,
    [isAdmin, editOrderPageViewStore.journey?.status, editOrderPageViewStore.order?.status],
  )

  //TODO Create a general loading error component
  if (orderError || visitError) {
    return <Typography variant='h4'>{t('errorLoadingOrder', 'Error loading the order')}</Typography>
  }

  return (
    <InitializationWrapper
      isInitializing={editOrderPageViewStore.isLoading || orderLoading || visitLoading}
    >
      <>
        <Helmet>
          <title>
            {t('orderDetailsTitle', 'Order details - {number}', {
              number: editOrderPageViewStore.displayAttributes.containerAttributes.number,
            })}
          </title>
        </Helmet>
        <Breadcrumbs items={getRouteBreadcrumbs()} />
        <EditOrderPageHeader editOrderPageViewStore={editOrderPageViewStore} />
        <Box
          paddingX={theme.customSpacing.l}
          paddingY={theme.customSpacing.m}
          gap={theme.customSpacing.m}
          display={'flex'}
          flexDirection={'column'}
          sx={{ background: theme.palette.background.neutral }}
          minHeight={'calc(100vh - 114px)'}
        >
          <OrderUpdateNotification
            onMarkAllAsRead={async () => {
              try {
                await orderUpdatesViewStore.markAllAsRead()

                appViewStore.setShowAlert(
                  'success',
                  t('markAsReadSuccessfully', 'Marked as read successfully'),
                )
              } catch (error) {
                appViewStore.setShowAlert(
                  'error',
                  t('failedToMarkUpdateAsRead', 'Failed to mark update as read'),
                )
              }
            }}
            updates={updates}
          />
          {isOrderFulfilledAndUserNonAdmin && (
            <Alert severity='warning' sx={{ marginBottom: theme.customSpacing.m }}>
              {t('cannotEditOrderWithXStatus', 'You cannot edit an order with status "{status}"', {
                status: editOrderPageViewStore.journey?.status,
              })}
            </Alert>
          )}
          <ReviewForm
            orderId={orderId}
            containerJourneyId={editOrderPageViewStore.journey?.data.id}
            hasServiceOrders={editOrderPageViewStore.hasServiceOrders}
            formStore={formStore}
            initialValues={editOrderPageViewStore.initialValues}
            displayAttributes={editOrderPageViewStore.displayAttributes}
            onSubmit={onSubmit}
            hideActions={isOrderFulfilledAndUserNonAdmin}
            cargoFormIntoEditMode={editOrderPageViewStore.cargoFormIntoEditMode}
            setCargoFormIntoEditMode={editOrderPageViewStore.setCargoFormIntoEditMode}
            holdsStore={editOrderPageViewStore.holdsStore}
          />
        </Box>
      </>
    </InitializationWrapper>
  )
})
